import { Component, Inject, OnInit, ViewChild } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { FilterCriteriaService, FilterCriterionModel, FilterGroupModel, FilterGroupsService } from "../../api";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { FilterGroupValidator } from "../../validators/filter-group-validator";
import { HttpLoaderService, ListOptions, SearchDataRequest, ValidationMessage } from "@tymes4-shared";
import { KeyValuePair } from "../../models/key-value-pair";
import { GenericErrorHandler } from "../../helpers/generic-error-handler";
import { ConfirmFilterDeletionComponent } from "../confirm-filter-deletion/confirm-filter-deletion.component";

const integerValidators = [Validators.required, Validators.pattern("-?[0-9]+"), Validators.min(-2147483648), Validators.max(2147483647)];

@Component({
    selector: 'app-edit-filter-group-form',
    templateUrl: './edit-filter-group-form.component.html',
    styleUrls: ['./edit-filter-group-form.component.scss'],
    standalone: false
})
export class EditFilterGroupComponent implements OnInit {

  @ViewChild('criteriaListVw') criteriaListVw: any; // can't use ListViewComponent type here because of circular dependency, but we only need the Data and refreshData properties/methods

  public form: FormGroup;

  // Note: this contains the *original* criterion data, plus empty objects for new criteria.
  // The form contains the actual user input to be submitted.
  public criteria: FilterCriterionModel[] = [];

  public showIdColumn = false;
  public saving = false;

  private nextFakeId = -1; // use negative ids for new criteria

  public validationMessages: KeyValuePair<Array<ValidationMessage>> = {
    DisplayOrdinalNumber: [
      { type: 'pattern', message: 'LOCATIONS.FILTER-GROUPS.DIALOG.FORM.VALIDATION.DISPLAY-ORDINAL-NUMBER.INTEGER' },
    ]
  };

  public criteriaListOptions: ListOptions = {
    search: false,
    pageSize: 6,
    renderInCard: false,
    noItemsText: 'LOCATIONS.FILTER-GROUPS.DIALOG.CRITERIA-LISTVIEW.NO-ITEMS',
    headers: [
      // Id column will be added here if needed in ngOnInit
      { sortField: 'Code', label: 'LOCATIONS.FILTER-GROUPS.DIALOG.CRITERIA-LISTVIEW.HEADER.CODE', width: null },
      { sortField: 'DisplayOrdinalNumber', label: 'LOCATIONS.FILTER-GROUPS.DIALOG.CRITERIA-LISTVIEW.HEADER.DISPLAY-ORDINAL-NUMBER', width: 110, isDefaultSort: 'asc' },
      { sortField: 'FriendlyName', label: 'LOCATIONS.FILTER-GROUPS.DIALOG.CRITERIA-LISTVIEW.HEADER.FRIENDLY-NAME', width: null },
      { label: '' }
    ]
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public passedData: { title: string, item: FilterGroupModel },
    public dialogRef: MatDialogRef<EditFilterGroupComponent>,
    private filterGroupValidator: FilterGroupValidator,
    private filterGroupsService: FilterGroupsService,
    private filterCriteriaService: FilterCriteriaService,
    private matDialog: MatDialog,
    private errorHandler: GenericErrorHandler,
    private loader: HttpLoaderService
  ) {}

  ngOnInit(): void {
    this.filterGroupValidator.filterGroupId = this.passedData.item?.Id;
    this.filterGroupValidator.filterCriterionIds = this.passedData.item?.Criteria?.map(c => c.Id);

    this.form = new FormGroup({
      Id: new FormControl(0),
      Code: new FormControl('', {
        validators: Validators.required,
        asyncValidators: [
          this.filterGroupValidator.groupCodeUnique
        ]
      }),
      FriendlyName: new FormControl('', [Validators.required]),
      DisplayOrdinalNumber: new FormControl('', integerValidators)
    }, {
      asyncValidators: this.filterGroupValidator.criterionCodesUnique
    });

    this.form.patchValue(this.passedData.item);

    if (Array.isArray(this.passedData.item?.Criteria)) {
      // copy the array so that edits in the dialog don't affect the filter groups overview
      this.criteria = [...this.passedData.item.Criteria];

      this.showIdColumn = this.criteria.some(c => c.Id > 0);
      if (this.showIdColumn) {
        this.criteriaListOptions.headers.unshift({ sortField: 'Id', label: 'LOCATIONS.FILTER-GROUPS.DIALOG.CRITERIA-LISTVIEW.HEADER.ID', width: 40 });
      }

      for (let criterion of this.criteria) {
        this.addCriterionToForm(criterion);
      }
    }
  }

  public getCriterionFormGroupName(criterion: FilterCriterionModel) {
    return "Criterion" + criterion.Id;
  }

  private getCriterionControl(criterion: FilterCriterionModel, property: keyof FilterCriterionModel) {
    const formGroupName = this.getCriterionFormGroupName(criterion);
    return this.form.get(formGroupName)?.get(property);
  }

  private getCriterionProperty(criterion: FilterCriterionModel, property: keyof FilterCriterionModel) {
    return this.getCriterionControl(criterion, property)?.value;
  }

  private addCriterionToForm(criterion: FilterCriterionModel) {
    const name = this.getCriterionFormGroupName(criterion);
    this.form.addControl(name, new FormGroup({
      Id: new FormControl(criterion.Id),
      Code: new FormControl(criterion.Code, [Validators.required]),
      FriendlyName: new FormControl(criterion.FriendlyName, [Validators.required]),
      DisplayOrdinalNumber: new FormControl(criterion.DisplayOrdinalNumber, integerValidators)
    }));
  }
  
  private removeCriterionFromForm(criterion: FilterCriterionModel) {
    const name = this.getCriterionFormGroupName(criterion);
    this.form.removeControl(name);
  }

  public addCriterion() {
    const newCriterion: FilterCriterionModel = {
      Id: this.nextFakeId--,
      FilterGroupId: this.passedData.item?.Id ?? 0,
      Code: '',
      FriendlyName: '',
      DisplayOrdinalNumber: this.criteria.length > 0 ? Math.max(...this.criteria.map(c => c.DisplayOrdinalNumber)) + 1 : 1
    };

    this.addCriterionToForm(newCriterion);
    this.criteria.push(newCriterion);

    this.criteriaListVw.refreshData();
  }

  public onCriteriaDataRequest(request: SearchDataRequest) {
    const filteredCriteria = request.searchText
      ? this.criteria.filter(criterion =>
          this.getCriterionProperty(criterion, 'Code').toLowerCase().includes(request.searchText.toLowerCase()) ||
          this.getCriterionProperty(criterion, 'FriendlyName').toLowerCase().includes(request.searchText.toLowerCase()))
      : this.criteria;

    if (request.sortField) {
      const sortField = request.sortField as keyof FilterCriterionModel;
      filteredCriteria.sort((criterion1, criterion2) => compare(this.getCriterionProperty(criterion1, sortField), this.getCriterionProperty(criterion2, sortField), request.sortAsc));
    }

    this.criteriaListVw.Data = {
      TotalRecords: this.criteria.length,
      Records: filteredCriteria.slice((request.pageNr - 1) * request.pageSize, request.pageNr * request.pageSize)
    };
  }

  public tryDeleteCriterion(criterion: FilterCriterionModel) {
    if (criterion.Id <= 0) {
      this.deleteCriterion(criterion);
      return;
    }

    this.loader.open();
    this.filterCriteriaService.getFilterCriterionUsages(criterion.Id).subscribe({
      next: usages => {
        this.loader.close();

        if (usages.length == 0) {
          this.deleteCriterion(criterion);
          return;
        }

        const options = {
          disableClose: true,
          panelClass: 'confirmBox',
          data: {
            title: 'LOCATIONS.FILTER-GROUPS.CONFIRM-DELETION.TITLE.FILTER-CRITERION',
            friendlyName: criterion.FriendlyName,
            usages: usages
          }
        };
        const dialogRef: MatDialogRef<any> = this.matDialog.open(ConfirmFilterDeletionComponent, options);
        dialogRef.afterClosed().subscribe(confirmed => {
          if (confirmed) {
            this.deleteCriterion(criterion);
          }
        });
      },
      error: err => {
        this.errorHandler.handle(err);
      }
    });
  }

  private deleteCriterion(criterion: FilterCriterionModel) {
    const index = this.criteria.findIndex(c => c.Id === criterion.Id);
    if (index == -1) {
      console.warn("Unknown criterion.", criterion);
      return;
    }

    this.criteria.splice(index, 1);
    this.removeCriterionFromForm(criterion);

    this.form.markAsDirty();
    this.criteriaListVw.refreshData();
  }
  
  public getGroupTranslationCode() {
    const code = this.form.get('Code')?.value.toUpperCase() ?? "";
    return "FILTER-GROUP." + code;
  }

  public isCodeInvalid(criterion: FilterCriterionModel) {
    const control = this.getCriterionControl(criterion, 'Code');
    if (control?.dirty && control?.invalid) {
      return true;
    }

    const code = control?.value;
    const takenCodes: string[] = this.form.getError('criterionCodesTaken');
    const duplicateCodes: string[] = this.form.getError('duplicateCodes');
    return takenCodes?.includes(code) || duplicateCodes?.includes(code);
  }

  public isDisplayOrdinalNumberInvalid(criterion: FilterCriterionModel) {
    const control = this.getCriterionControl(criterion, 'DisplayOrdinalNumber');
    return control?.dirty && control?.invalid;
  }

  public isFriendlyNameInvalid(criterion: FilterCriterionModel) {
    const control = this.getCriterionControl(criterion, 'FriendlyName');
    return control?.dirty && control?.invalid;
  }

  public submit() {
    this.saving = true;

    const data = this.form.value as FilterGroupModel;
    data.Code = data.Code?.toUpperCase() ?? "";

    data.Criteria = [];
    for (let criterion of this.criteria) {
      const groupName = this.getCriterionFormGroupName(criterion);
      const criterionData = this.form.get(groupName)?.value as FilterCriterionModel;
      criterionData.FilterGroupId = this.passedData.item?.Id ?? 0;
      criterionData.Code = criterionData.Code?.toUpperCase() ?? "";

      data.Criteria.push(criterionData);
      data[groupName] = undefined; // no need to send this to the backend
    }

    var observable = this.passedData.item == null
      ? this.filterGroupsService.createFilterGroup(data)
      : this.filterGroupsService.updateFilterGroup(this.passedData.item.Id, data);

    this.loader.open();
    observable.subscribe({
      next: id => {
        this.loader.close();
        this.dialogRef.close(id ?? data.Id);
      },
      error: (err: any) => {
        this.saving = false;
        this.errorHandler.handle(err);
      }
    });
  }
}

function compare(value1: any, value2: any, sortAsc: boolean) {
  var comparison = typeof value1 === 'string' && typeof value2 === 'string'
    ? value1.localeCompare(value2)
    : (value1 < value2 ? -1 : (value1 > value2 ? 1 : 0));

  if (sortAsc === false) {
    comparison = -comparison;
  }
  return comparison;
}
